import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@kognitos/lattice';
import { captureException } from '@/modules/sentry';

import { decodeOAuthCallbackError, parseOAuthCallbackError } from '../utils';

/**
 * Shared toast id so several connection widgets reacting to the same `?error=`
 * param collapse into one toast instead of stacking one per widget.
 */
const OAUTH_ERROR_TOAST_ID = 'oauth-connection-error';

export type TShowOAuthCallbackError = (
  rawError: string,
  component: string,
) => void;

/**
 * Handles the `?error=` param the OAuth callback puts on the return URL.
 *
 * Duplicate-connection failures are reported as informational rather than as
 * errors, because the connection the user signed in for already exists —
 * nothing they did failed. Nothing goes to Sentry.
 *
 * The copy is deliberately narrow on two counts:
 *  - It names no connection. The usual trigger is a replayed callback, where
 *    the user only ever made one connection, so pointing at a specific one
 *    would confuse more than it helps.
 *  - It claims only that the connection exists in the workspace, never that
 *    the step in front of the user is finished. On this path the callback
 *    returns no `data` param, so the automation and chat widgets never link or
 *    select the existing connection and their integration step still renders
 *    as "Not connected". Wording that implies completion contradicts what the
 *    user is looking at and stops them from finishing the job by hand.
 *
 * Everything else keeps the generic failure toast and is reported, with the
 * message decoded first so it is readable in Sentry rather than a wall of
 * percent escapes.
 *
 * @returns Callback taking the raw `error` param and the reporting component's
 *   name, used as the Sentry `component` tag
 */
export function useOAuthCallbackError(): TShowOAuthCallbackError {
  const { t: tIntegrations } = useTranslation('integrations');

  return useCallback(
    (rawError: string, component: string) => {
      const duplicate = parseOAuthCallbackError(rawError);

      if (duplicate) {
        toast.info(
          tIntegrations(
            duplicate.reason === 'credentials'
              ? 'integrations.addConnection.oauthDuplicateCredentials'
              : 'integrations.addConnection.oauthDuplicateName',
          ),
          { id: OAUTH_ERROR_TOAST_ID },
        );
        return;
      }

      captureException(new Error(decodeOAuthCallbackError(rawError)), {
        tags: { component, action: 'oauth_error' },
      });
      toast.error(tIntegrations('integrations.addConnection.oauthError'), {
        id: OAUTH_ERROR_TOAST_ID,
      });
    },
    [tIntegrations],
  );
}
